import {
	useRef,
	useEffect,
	useState,
	type CSSProperties,
	type ReactNode,
} from "react";

interface FadeInProps {
	children: ReactNode;
	className?: string;
	delay?: number;
	duration?: number;
	translateY?: number;
	/** Skip Intersection Observer — reveal immediately after delay (for above-the-fold content) */
	immediate?: boolean;
}

export function FadeIn({
	children,
	className = "",
	delay = 0,
	duration = 700,
	translateY = 28,
	immediate = false,
}: FadeInProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (immediate) return;

		const el = ref.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					if (delay) {
						setTimeout(() => setVisible(true), delay);
					} else {
						setVisible(true);
					}
					observer.unobserve(el);
				}
			},
			{ threshold: 0.15 },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [delay, immediate]);

	if (immediate) {
		const immediateStyle = {
			opacity: 0,
			transform: `translateY(${translateY}px)`,
			"--fade-in-from-y": `${translateY}px`,
			animationName: "fade-in-rise",
			animationDuration: `${duration}ms`,
			animationTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
			animationDelay: `${delay}ms`,
			animationFillMode: "both",
		} as CSSProperties;

		return (
			<div
				className={`fade-in-immediate ${className}`.trim()}
				style={immediateStyle}
			>
				{children}
			</div>
		);
	}

	return (
		<div
			ref={ref}
			className={className}
			style={{
				opacity: visible ? 1 : 0,
				transform: visible
					? "translateY(0)"
					: `translateY(${translateY}px)`,
				transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1), transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1)`,
				transitionDelay: visible ? "0ms" : `${delay}ms`,
			}}
		>
			{children}
		</div>
	);
}
